import { useMemo } from 'react';

import {
  getLastTreatmentEpisodeClosedDate,
  hasFutureClosedDate,
} from '../util/episodes.util';
import { isUniversalStatusOpen } from '../util/patient.util';

interface UseAppointmentBookingEligibilityParams {
  patient: Patient;
  treatmentEpisodes?: TreatmentEpisode[];
}

interface UseAppointmentBookingEligibilityState {
  isBookingEnabled: boolean;
}

const isSupportBookingEnabled = (
  patient: Patient,
  isProxyRequired: boolean,
): boolean => {
  return (patient?.supports ?? []).some(
    ({ accountActive, isProxy }: Support) =>
      Boolean(accountActive) && (!isProxyRequired || isProxy),
  );
};

export const isPatientBookingEnabled = (patient: Patient): boolean => {
  return (
    !!patient?.isAccountActive ||
    // allow booking with "offline" patients who have a proxy support with an active account
    isSupportBookingEnabled(patient, true)
  );
};

const isBookingEnabled = (
  patient: Patient,
  treatmentEpisodes: TreatmentEpisode[],
): boolean =>
  (isUniversalStatusOpen(patient.universalStatusCode) ||
    hasFutureClosedDate(
      getLastTreatmentEpisodeClosedDate(treatmentEpisodes),
    )) &&
  (isPatientBookingEnabled(patient) || isSupportBookingEnabled(patient, false));

/**
 * This hook uses the universal patient status and treatment episode data to determine if a provider can
 * book an appointment with the patient or support.
 */
const useAppointmentBookingEligibility = ({
  patient,
  treatmentEpisodes,
}: UseAppointmentBookingEligibilityParams): UseAppointmentBookingEligibilityState => {
  return useMemo<UseAppointmentBookingEligibilityState>(
    () => ({
      isBookingEnabled: isBookingEnabled(patient, treatmentEpisodes),
    }),
    [patient, treatmentEpisodes],
  );
};

export default useAppointmentBookingEligibility;
